import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Cpu, Power, RotateCcw } from "lucide-react";
import { api, ApiError, okSchema } from "@/lib/api";
import { PageHeader } from "@/components/layout/AppShell";
import { Card, CardSection } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { StatTile } from "@/components/ui/StatTile";
import { Alert, LoadingPanel } from "@/components/ui/Feedback";
import { useToast } from "@/components/ui/toast";
import { useSession, isAdmin } from "@/lib/session";
import { cn } from "@/lib/cn";

const modelSchema = z.looseObject({
  model: z.string(),
  tasks: z.array(z.string()).default([]),
  token_limit: z.number(),
  tokens_used: z.number(),
  tokens_remaining: z.number(),
  percent_used: z.number(),
  enabled: z.boolean(),
  calls: z.number().default(0),
  exhausted: z.boolean().default(false),
});

const listSchema = z.looseObject({
  items: z.array(modelSchema).default([]),
  active: z.record(z.string(), z.string().nullable()).default({}),
  tasks: z.record(z.string(), z.array(z.string())).default({}),
});

type Model = z.infer<typeof modelSchema>;

/** Nama tugas dalam bahasa yang berarti bagi operator, bukan kunci internal. */
const TASK_LABEL: Record<string, string> = {
  text_reasoning: "Chat & analisis",
  text_fast: "Skrining cepat",
  vision: "Gambar di chat",
  vision_mri: "MRI Assist",
  ocr: "Baca teks gambar",
  image_gen: "Buat gambar",
  tts: "Suara",
};

function formatNumber(value: number) {
  return value.toLocaleString("id-ID");
}

export function AdminAiModels() {
  const { data: session } = useSession();
  const isSuperadmin = isAdmin(session) && session.role === "superadmin";
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data, isPending, error } = useQuery({
    queryKey: ["admin", "ai-models"],
    queryFn: ({ signal }) => api.get("/api/admin/ai-models", listSchema, signal),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["admin", "ai-models"] });

  const toggle = useMutation({
    mutationFn: (input: { model: string; enabled: boolean }) =>
      api.post(`/api/admin/ai-models/${encodeURIComponent(input.model)}/toggle`, okSchema, {
        enabled: input.enabled,
      }),
    onSuccess: (_result, input) => {
      toast.success({
        title: input.enabled ? "Model diaktifkan" : "Model dimatikan",
        description: input.enabled
          ? `${input.model} kembali masuk antrian.`
          : `${input.model} tidak akan dipakai lagi.`,
      });
      invalidate();
    },
    onError: (cause) =>
      toast.danger({
        title: "Gagal mengubah status",
        description: cause instanceof ApiError ? cause.message : "Coba lagi.",
      }),
  });

  const reset = useMutation({
    mutationFn: (model: string) =>
      api.post(`/api/admin/ai-models/${encodeURIComponent(model)}/reset`, okSchema, {}),
    onSuccess: (_result, model) => {
      toast.success({ title: "Penghitung direset", description: `${model} kembali ke nol.` });
      invalidate();
    },
  });

  if (isPending) {
    return (
      <>
        <PageHeader eyebrow="Sistem" title="Model AI" />
        <Card>
          <LoadingPanel label="Memuat daftar model" />
        </Card>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <PageHeader eyebrow="Sistem" title="Model AI" />
        <Alert tone="danger" title="Gagal memuat daftar model">
          {error instanceof ApiError ? error.message : "Terjadi kesalahan tak terduga."}
        </Alert>
      </>
    );
  }

  const used = data.items.filter((item) => item.tasks.length > 0);
  const off = data.items.filter((item) => !item.enabled).length;
  const empty = data.items.filter((item) => item.tokens_remaining <= 0).length;
  const totalRemaining = used.reduce((sum, item) => sum + item.tokens_remaining, 0);

  return (
    <>
      <PageHeader
        eyebrow="Sistem"
        title="Model AI"
        description="Sisa token tiap model dan saklar untuk mematikannya. Model yang dimatikan tidak akan pernah dipakai."
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Model dipakai" value={used.length} icon={<Cpu className="size-4" />} />
        <StatTile label="Sisa token" value={formatNumber(totalRemaining)} />
        <StatTile
          label="Token habis"
          value={empty}
          className={cn(empty > 0 && "border-urgent-500/40")}
        />
        <StatTile
          label="Dimatikan admin"
          value={off}
          className={cn(off > 0 && "border-critical-500/40")}
        />
      </div>

      {!isSuperadmin && (
        <Alert tone="info" className="mb-4">
          Hanya superadmin yang dapat mematikan atau mereset model. Anda tetap dapat memantau sisa
          token di bawah.
        </Alert>
      )}

      <Card className="overflow-hidden">
        {Object.entries(data.tasks).map(([task, pool], index) => {
          const active = data.active[task];
          const rows = pool
            .map((name) => data.items.find((item) => item.model === name))
            .filter((item): item is Model => Boolean(item));

          return (
            <CardSection
              key={task}
              eyebrow={`Antrian ${index + 1}`}
              title={TASK_LABEL[task] ?? task}
              className={cn(index === 0 && "border-t-0")}
              action={
                active ? (
                  <Badge tone="accent">aktif: {active}</Badge>
                ) : (
                  <Badge tone="critical">tidak ada model tersedia</Badge>
                )
              }
            >
              <ul className="divide-hairline divide-y">
                {rows.map((item) => {
                  const isActive = item.model === active;
                  return (
                    <li
                      key={item.model}
                      className="flex flex-wrap items-center gap-x-4 gap-y-2 py-2.5"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                          <span className="font-mono">{item.model}</span>
                          {isActive && <Badge tone="accent">dipakai</Badge>}
                          {!item.enabled && <Badge tone="critical">dimatikan</Badge>}
                          {item.enabled && item.tokens_remaining <= 0 && (
                            <Badge tone="urgent">token habis</Badge>
                          )}
                        </p>
                        {/* Bar pemakaian: angka saja sulit dibandingkan sekilas
                            ketika ada tiga puluhan model. */}
                        <div className="bg-surface-sunken mt-1.5 h-1.5 w-full max-w-md overflow-hidden rounded-full">
                          <div
                            className={cn(
                              "h-full rounded-full",
                              item.tokens_remaining <= 0
                                ? "bg-critical-500"
                                : item.percent_used > 80
                                  ? "bg-urgent-500"
                                  : "bg-accent",
                            )}
                            style={{ width: `${Math.min(100, item.percent_used)}%` }}
                          />
                        </div>
                        <p className="text-content-subtle mt-1 text-xs">
                          {formatNumber(item.tokens_remaining)} tersisa dari{" "}
                          {formatNumber(item.token_limit)} · {item.calls} panggilan
                        </p>
                      </div>

                      {isSuperadmin && (
                        <div className="flex shrink-0 items-center gap-2">
                          <Button
                            size="sm"
                            icon={<RotateCcw className="size-3.5" />}
                            onClick={() => reset.mutate(item.model)}
                            loading={reset.isPending && reset.variables === item.model}
                          >
                            Reset
                          </Button>
                          <Button
                            size="sm"
                            variant={item.enabled ? "ghost" : "primary"}
                            icon={<Power className="size-3.5" />}
                            onClick={() =>
                              toggle.mutate({ model: item.model, enabled: !item.enabled })
                            }
                            loading={
                              toggle.isPending && toggle.variables?.model === item.model
                            }
                          >
                            {item.enabled ? "Matikan" : "Aktifkan"}
                          </Button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </CardSection>
          );
        })}
      </Card>

      <p className="text-content-muted mt-3 text-xs">
        Pemakaian dihitung dari laporan token penyedia pada setiap panggilan. Ketika token sebuah
        model habis, permintaan berikutnya otomatis memakai model berikutnya pada antrian yang sama.
      </p>
    </>
  );
}
